import Navbar from '../../home/components/Navbar';
import Footer from '../../home/components/Footer';
import RelatedServices from '../../../components/feature/RelatedServices';
import RelatedIndustries from '../../../components/feature/RelatedIndustries';

export default function MarketingAutomationPage() {
  const features = [
    'Lead Capture & Tracking',
    'Automated Email Sequences',
    'SMS Appointment Reminders',
    'Patient Nurture Campaigns',
    'CRM Integration',
    'Pipeline Management',
    'Automated Follow-ups',
    'Booking Workflow Automation',
    'Lead Scoring',
    'Multi-Channel Communication',
    'Reporting & Analytics',
    'HIPAA-Compliant Systems'
  ];

  const automations = [
    {
      icon: 'ri-mail-send-line',
      title: 'Lead Nurture Sequences',
      description: 'Automatically educate and nurture every lead with personalized email sequences until they book.'
    },
    {
      icon: 'ri-message-3-line',
      title: 'SMS Appointment Reminders',
      description: 'Reduce no-shows with automated text reminders sent before every appointment.'
    },
    {
      icon: 'ri-phone-line',
      title: 'Missed Call Follow-up',
      description: 'Automatically text or email patients who call when you\'re busy so no lead falls through the cracks.'
    },
    {
      icon: 'ri-calendar-check-line',
      title: 'Booking Confirmations',
      description: 'Instant automated confirmations with appointment details, directions, and preparation instructions.'
    },
    {
      icon: 'ri-star-line',
      title: 'Review Request Automation',
      description: 'Automatically request reviews from satisfied patients at the perfect time after their visit.'
    },
    {
      icon: 'ri-user-follow-line',
      title: 'Re-engagement Campaigns',
      description: 'Automatically reach out to inactive patients to bring them back for follow-up care.'
    }
  ];

  const benefits = [
    {
      icon: 'ri-time-line',
      title: 'Save Hours Every Week',
      description: 'Automation handles repetitive tasks so your team can focus on patient care instead of admin work.'
    },
    {
      icon: 'ri-user-add-line',
      title: 'Never Miss a Lead',
      description: 'Every inquiry gets immediate follow-up, even when your team is busy with patients.'
    },
    {
      icon: 'ri-line-chart-line',
      title: 'Increase Conversion Rates',
      description: 'Consistent, timely follow-up converts more leads into booked appointments and patients.'
    },
    {
      icon: 'ri-calendar-line',
      title: 'Reduce No-Shows',
      description: 'Automated reminders significantly decrease missed appointments and lost revenue.'
    },
    {
      icon: 'ri-funds-line',
      title: 'Maximize Patient Value',
      description: 'Automated re-engagement brings patients back for ongoing care and additional services.'
    },
    {
      icon: 'ri-shield-check-line',
      title: 'HIPAA-Compliant',
      description: 'All automation systems are implemented with healthcare privacy and security standards.'
    }
  ];

  const process = [
    {
      step: '01',
      title: 'Workflow Analysis',
      description: 'Map your current patient journey and identify opportunities for automation and improvement.'
    },
    {
      step: '02',
      title: 'System Setup',
      description: 'Implement and configure CRM, email marketing, SMS, and automation tools for your practice.'
    },
    {
      step: '03',
      title: 'Automation Building',
      description: 'Create custom automated workflows for lead nurturing, appointment reminders, and follow-ups.'
    },
    {
      step: '04',
      title: 'Integration',
      description: 'Connect automation with your website, booking system, and practice management software.'
    },
    {
      step: '05',
      title: 'Team Training',
      description: 'Train your staff on using the CRM and automation tools effectively in daily operations.'
    },
    {
      step: '06',
      title: 'Optimization',
      description: 'Monitor performance and continuously refine automations for better results over time.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6 lg:px-12 bg-gradient-to-br from-[#F5F3FF] via-white to-[#FAF5FF] relative overflow-hidden">
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-[#7C3AED] font-bold mb-4">
                MARKETING AUTOMATION &amp; CRM
              </div>
              <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 leading-tight mb-6">
                Never Miss a Lead. Automate Patient Follow-Up &amp; Nurturing
              </h1>
              <p className="text-lg text-gray-600 leading-relaxed mb-8">
                HIPAA-compliant marketing automation that nurtures every lead, reduces no-shows, and maximizes patient lifetime value while your team focuses on care.
              </p>
              <div className="flex flex-wrap gap-4">
                <a
                  href="/book-strategy-call"
                  className="inline-flex items-center gap-2 bg-gradient-to-r from-[#7C3AED] to-[#A855F7] text-white px-8 py-4 rounded-lg hover:shadow-lg transition-all cursor-pointer whitespace-nowrap font-bold"
                >
                  <span>Get Your Automation Plan</span>
                  <i className="ri-arrow-right-line"></i>
                </a>
                <a
                  href="#process"
                  className="inline-flex items-center gap-2 bg-white text-[#7C3AED] border-2 border-[#7C3AED] px-8 py-4 rounded-lg hover:bg-[#7C3AED]/5 transition-all cursor-pointer whitespace-nowrap font-medium"
                >
                  <span>See Our Process</span>
                </a>
              </div>
            </div>
            <div className="relative">
              <div className="w-full h-96 rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="https://public.readdy.ai/ai/img_res/95e2929f826df314c55ae268ac684bbd.jpg"
                  alt="Marketing automation dashboard"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Automations Section */}
      <section className="py-24 px-6 lg:px-12 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="text-xs uppercase tracking-[0.2em] text-[#7C3AED] font-bold mb-4">
              AUTOMATIONS WE BUILD
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 leading-tight mb-6">
              Automate Every Step of the Patient Journey
            </h2>
            <p className="text-base text-gray-600 max-w-3xl mx-auto">
              From first contact to ongoing care, automation ensures no patient falls through the cracks and every opportunity is maximized.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {automations.map((automation, index) => (
              <div
                key={index}
                className="bg-[#F9FAFB] rounded-xl p-8 hover:shadow-xl transition-all duration-300 border border-gray-100"
              >
                <div className="w-14 h-14 bg-gradient-to-br from-[#7C3AED] to-[#A855F7] rounded-lg flex items-center justify-center mb-6">
                  <i className={`${automation.icon} text-2xl text-white`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  {automation.title}
                </h3>
                <p className="text-gray-600 leading-relaxed text-sm">
                  {automation.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-24 px-6 lg:px-12 bg-[#F9FAFB]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="text-xs uppercase tracking-[0.2em] text-[#7C3AED] font-bold mb-4">
              WHY AUTOMATION MATTERS
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 leading-tight mb-6">
              Work Smarter, Not Harder
            </h2>
            <p className="text-base text-gray-600 max-w-3xl mx-auto">
              Marketing automation transforms your practice operations, freeing up time while improving patient experience and revenue.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <div
                key={index}
                className="bg-white rounded-xl p-8 hover:shadow-xl transition-all duration-300 border border-gray-100"
              >
                <div className="w-14 h-14 bg-gradient-to-br from-[#7C3AED] to-[#A855F7] rounded-lg flex items-center justify-center mb-6">
                  <i className={`${benefit.icon} text-2xl text-white`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  {benefit.title}
                </h3>
                <p className="text-gray-600 leading-relaxed text-sm">
                  {benefit.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 px-6 lg:px-12 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-[#7C3AED] font-bold mb-4">
                COMPREHENSIVE PLATFORM
              </div>
              <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 leading-tight mb-6">
                Complete Marketing Automation Solution
              </h2>
              <p className="text-base text-gray-600 leading-relaxed mb-8">
                Everything you need to automate patient acquisition, nurturing, and retention in one HIPAA-compliant system.
              </p>
              <div className="grid sm:grid-cols-2 gap-4">
                {features.map((feature, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <div className="w-5 h-5 bg-[#7C3AED]/10 rounded-full flex items-center justify-center mt-0.5 flex-shrink-0">
                      <i className="ri-check-line text-[#7C3AED] text-sm"></i>
                    </div>
                    <span className="text-gray-700 text-sm font-medium">{feature}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <div className="w-full h-[500px] rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="https://readdy.ai/api/search-image?query=CRM%20automation%20platform%20showing%20lead%20pipeline%20patient%20journey%20workflows%20email%20automation%20SMS%20campaigns%20and%20analytics%20dashboard%20for%20healthcare%20practice%20professional%20medical%20automation%20software%20interface%20purple%20accents%20clean%20modern%20design&width=600&height=700&seq=automationfeatures1&orientation=portrait"
                  alt="Automation platform features"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section id="process" className="py-24 px-6 lg:px-12 bg-[#F9FAFB]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="text-xs uppercase tracking-[0.2em] text-[#7C3AED] font-bold mb-4">
              OUR PROCESS
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 leading-tight mb-6">
              From Setup to Optimization
            </h2>
            <p className="text-base text-gray-600 max-w-3xl mx-auto">
              We handle everything from system selection to implementation to ongoing optimization of your automation.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {process.map((item, index) => (
              <div
                key={index}
                className="bg-white rounded-xl p-8 hover:shadow-lg transition-all duration-300 border border-gray-100"
              >
                <div className="w-16 h-16 bg-gradient-to-br from-[#7C3AED] to-[#A855F7] rounded-xl flex items-center justify-center mb-6">
                  <span className="text-2xl font-bold text-white">{item.step}</span>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  {item.title}
                </h3>
                <p className="text-gray-600 leading-relaxed text-sm">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-6 lg:px-12 bg-gradient-to-br from-[#0D9488] to-[#14B8A6] relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-black/10 via-transparent to-black/10"></div>
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <h2 className="text-4xl lg:text-5xl font-bold text-white leading-tight mb-6">
            Ready to Automate Your Patient Acquisition?
          </h2>
          <p className="text-lg text-white/90 mb-8 leading-relaxed">
            Get a custom automation plan showing exactly how we'll streamline your operations and grow your practice.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <a
              href="/book-strategy-call"
              className="inline-flex items-center gap-2 bg-white text-[#0D9488] px-8 py-4 rounded-lg hover:bg-gray-50 transition-all cursor-pointer whitespace-nowrap font-bold"
            >
              <span>Get Your Automation Plan</span>
              <i className="ri-arrow-right-line"></i>
            </a>
            <a
              href="/#contact"
              className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm text-white border-2 border-white/30 px-8 py-4 rounded-lg hover:bg-white/20 transition-all cursor-pointer whitespace-nowrap font-medium"
            >
              <span>See Platform Demo</span>
            </a>
          </div>
        </div>
      </section>

      <RelatedServices currentSlug="marketing-automation" />
      <RelatedIndustries />

      <Footer />
    </div>
  );
}
